/**
 * ZUM ZIEL FUEHREN -- sichtbar machen und den Fokus setzen.
 *
 * HOMEBASE zeigt an mehreren Stellen eine LUECKE und bietet an, sie zu
 * schliessen: „Information ergaenzen" in der Produktakte, die fehlenden
 * Stammdaten in der Lieferantenakte. Der Weg dorthin ist jedes Mal
 * derselbe -- Bereich wechseln, Bearbeitungsmodus oeffnen, das richtige
 * Feld in den Blick holen, den Fokus hineinsetzen -- und er ist jedes
 * Mal an denselben Stellen zerbrechlich.
 *
 * DESHALB STEHT ER HIER EINMAL. Drei Fehler sind auf dem Weg dorthin
 * gemacht und gemessen worden; jeder hat eine Zeile hinterlassen:
 *
 *   1. FOKUS NACH WARTEZEIT. Eine erste Fassung setzte ihn 350 ms nach
 *      dem Sprung. Laedt die Ansicht in dieser Spanne nach, raeumt der
 *      Neuaufbau den Timer weg, und der Fokus bleibt aus. Gemessen an
 *      P60-4K. Reihenfolge statt Wartezeit.
 *
 *   2. WEICHER LAUF. Die zweite Fassung lief weich. Sie kam an -- nach
 *      gut zwei Sekunden, und nur, wenn niemand dazwischen scrollte.
 *      Eine einzige Radbewegung des Anwenders bricht einen weichen Lauf
 *      ENDGUELTIG ab; gemessen blieb der Behaelter bei 877 von 1291
 *      Pixeln stehen. Wer nichts geschehen sieht, scrollt selbst -- und
 *      verhindert damit genau den Sprung, auf den er wartet. Also
 *      ohne Animation.
 *
 *   3. DAS FENSTER IST NICHT DER AUSSCHNITT. Die Akte scrollt in einem
 *      eigenen Behaelter (`overflow: auto`), nicht im Browserfenster.
 *      Wer gegen `window.innerHeight` rechnet, misst am Gegenstand
 *      vorbei. Deshalb `scrollBehaelter` -- und danach wird NACHGESEHEN
 *      statt gehofft.
 */

/**
 * Der naechste Vorfahre, der wirklich scrollt.
 *
 * Gesucht wird nicht nach einer Klasse, sondern nach der EIGENSCHAFT:
 * ein Vorfahre, der ueberlaufenden Inhalt scrollt. Aendert jemand den
 * Aufbau einer Ansicht, findet diese Funktion den neuen Behaelter, ohne
 * ihn zu kennen.
 */
export function scrollBehaelter(el: HTMLElement): HTMLElement {
  for (let p = el.parentElement; p !== null; p = p.parentElement) {
    const o = getComputedStyle(p).overflowY;
    if ((o === "auto" || o === "scroll" || o === "overlay")
      && p.scrollHeight > p.clientHeight) return p;
  }
  return (document.scrollingElement ?? document.documentElement) as HTMLElement;
}

/**
 * Ein Ziel in den Blick holen und den Fokus hineinsetzen.
 *
 * `rahmen`  der Bereich, der sichtbar werden soll
 * `feld`    das Element, das den Fokus bekommt -- `null`, wenn nur
 *           gezeigt und nicht getippt werden soll
 *
 * Aufzurufen in einem Effekt; der Rueckgabewert ist die Aufraeumfunktion.
 */
/**
 * `danach` laeuft am ENDE des Bildaufbaus, nicht davor.
 *
 * Das ist kein Beiwerk, sondern der Grund, warum es diesen Parameter
 * gibt: wer eine Vorwahl gleich beim Aufruf verbraucht, loest ein
 * Neuzeichnen aus -- und die Aufraeumfunktion dieses Effekts bricht den
 * eben angeforderten Bildaufbau wieder ab. Gemessen: der Reiter stimmte,
 * der Modus stimmte, der Fokus lag auf BODY. Was den Sprung beendet,
 * gehoert deshalb HINTER den Sprung.
 */
export function zeigeUndFokussiere(
  rahmen: HTMLElement | null,
  feld: HTMLElement | null,
  danach?: () => void,
): () => void {
  if (rahmen === null) return () => { /* nichts zu tun */ };
  /* Ein Bildaufbau spaeter -- dann hat der Browser das eben eingehaengte
     Ziel wirklich vermessen. */
  const bild = requestAnimationFrame(() => {
    rahmen.scrollIntoView({ block: "center" });
    const kasten = scrollBehaelter(rahmen);
    const r = rahmen.getBoundingClientRect();
    /*
     * UND WENN DIE SEITE SELBST SCROLLT, IST IHR KASTEN NICHT IHR
     * AUSSCHNITT.
     *
     * `getBoundingClientRect()` des Wurzelelements liefert seine
     * Randbox -- bei gescrollter Seite also etwas wie -286 bis 527,
     * waehrend der Anwender 0 bis 813 sieht. Gegen diese Zahlen
     * gerechnet gilt ein Ziel bei 544 als „unterhalb des Ausschnitts",
     * obwohl es mitten im Bild steht; und umgekehrt bliebe ein wirklich
     * verdecktes Ziel unkorrigiert.
     *
     * Gemessen am Kontaktbereich der Lieferantenakte (SU-U6). Dieselbe
     * Lehre wie oben unter 3, nur andersherum: fuer einen eigenen
     * Behaelter gilt seine Randbox, fuer die SEITE gilt das Fenster.
     */
    const seite = kasten === (document.scrollingElement ?? document.documentElement);
    const k = seite
      ? { top: 0, bottom: window.innerHeight }
      : kasten.getBoundingClientRect();
    /* Nachgesehen, nicht gehofft: liegt das Ziel noch ueber oder unter
       dem sichtbaren Ausschnitt, wird der Behaelter selbst nachgezogen.
       Ein gesetzter `scrollTop` laesst sich nicht abbrechen und nicht
       beschneiden. */
    if (r.bottom > k.bottom) kasten.scrollTop += r.bottom - k.bottom;
    else if (r.top < k.top) kasten.scrollTop -= k.top - r.top;
    /* FOKUS OHNE EIGENEN SPRUNG. `focus()` scrollt sonst selbst, hart
       und an eine andere Stelle als eben berechnet. */
    feld?.focus({ preventScroll: true });
    danach?.();
  });
  return () => cancelAnimationFrame(bild);
}
